import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

const HUBSPOT_PROFILE_CREATION_DELAY = 5000
const HUBSPOT_TOKEN_RETRY_DELAY = 3000
const HUBSPOT_TOKEN_MAX_RETRIES = 5

const getProfileReadyAt = (createdDate: string | null | undefined): number => {
  if (!createdDate) return 0

  const createdAt = new Date(createdDate).getTime()
  if (Number.isNaN(createdAt)) return 0

  return createdAt + HUBSPOT_PROFILE_CREATION_DELAY
}

export const useHubspotToken = (): string | undefined => {
  const apiClient = useApiClient(ClientApiClient)
  const [auth] = useContext(AuthSessionContext)
  const { account } = useAccountReadContext()
  const accountId = account?.id
  const sessionId = auth?.sessionId
  const profileReadyAt = getProfileReadyAt(account?.createdDate)
  const [currentTime, setCurrentTime] = useState(Date.now)

  useEffect(() => {
    if (!accountId || !sessionId) return
    const delay = profileReadyAt - Date.now()

    if (delay <= 0) {
      setCurrentTime(Date.now())
      return
    }

    const timeoutId = setTimeout(() => {
      setCurrentTime(profileReadyAt)
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [accountId, profileReadyAt, sessionId])

  const enabled = !!accountId && !!sessionId && currentTime >= profileReadyAt
  const { data: token } = useQuery({
    queryKey: queryKeys.account.hubspotToken(accountId, sessionId),
    queryFn: async () => {
      try {
        const response = await apiClient.getHBToken()
        if (!response?.token) return
        return response.token
      } catch (error: unknown) {
        if (error instanceof ResponseError) error.preventDefault()
        throw error
      }
    },
    enabled,
    retry: HUBSPOT_TOKEN_MAX_RETRIES,
    retryDelay: HUBSPOT_TOKEN_RETRY_DELAY,
    staleTime: Infinity,
    refetchOnReconnect: false,
    throwOnError: false,
  })

  return enabled ? token : undefined
}
